import { joiResolver } from '@hookform/resolvers/joi';
import { useTheme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import {
  Box,
  Button,
  CustomDialog,
  IconButton,
  IconName,
  InputLabel,
  SubmitActions,
  TextFieldForm,
  useSnackbarHandler,
} from 'web-library';
import {
  JobStatusReportEmailPayload,
  usePostAdminJobStatusReportEmail
} from '../../../../api-refactored/job-status-report/usePostAdminJobStatusReportEmail';
import jobStatusReportEmailSchema from '../../schema/jobStatusReportEmailSchema';

const useStyles = makeStyles()((theme) =>
({
  deleteIcon: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
  emailContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: theme.spacing(2),
  },
  addContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
}));
interface JobStatusReportEmailModalProps {
  uuid: string;
  open: boolean;
  closeModal: () => void;
}

const DEFAULT_VALUE = {
  password: '',
  emails: [''],
};

const JobStatusReportEmailModal = ({ uuid, open, closeModal }: JobStatusReportEmailModalProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const [mutationStatus, setMutationStatus] = useState<MutationStatus>('idle');
  const { snackbarHandler } = useSnackbarHandler();

  const { mutate: sendEmailMutate } = usePostAdminJobStatusReportEmail({
    onSuccess: () => {
      setMutationStatus('idle');
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>JobStatusReport.EmailModal.EmailSentMessage'),
      });
      reset(DEFAULT_VALUE);
      closeModal();
    },
    onError: () => {
      if (open) {
        setMutationStatus('error');
      }
    },
  });

  const form = useForm<JobStatusReportEmailPayload>({
    mode: 'onSubmit',
    resolver: joiResolver(jobStatusReportEmailSchema),
    defaultValues: DEFAULT_VALUE,
  });
  const { formState: { errors }, handleSubmit, control, reset } = form;

  const { fields, append: appendEmail, remove: removeEmail } = useFieldArray<JobStatusReportEmailPayload>({
    control,
    name: 'emails',
  });

  const onConfirm = useCallback(
    (data: any) => {
      setMutationStatus('loading');
      sendEmailMutate({
        uuid,
        emails: data.emails,
        password: data.password,
      });
    },
    [sendEmailMutate, uuid]
  );

  const onCancel = useCallback(() => {
    closeModal();
    setMutationStatus('idle');
    reset(DEFAULT_VALUE);
  }, [closeModal, reset]);

  const onDelete = useCallback(
    async (index: number) => {
      await removeEmail(index);
      form.setValue('emails',form.getValues('emails').filter(e=>e))
    },
    [removeEmail]
  );

  const onAdd = useCallback(() => {
    appendEmail('' as any);
  }, [appendEmail]);

  const isDeletable = useMemo(() => fields.length > 1, [fields.length]);

  const formWrapper = useCallback(
    (children: any) => <form onSubmit={handleSubmit(onConfirm, (e)=>console.log(e, form.getValues('emails'), fields))}>{children}</form>,
    [handleSubmit, onConfirm],
  )

  useEffect(()=>{
    reset(DEFAULT_VALUE);
  }, [open]);

  return (
    (<CustomDialog
      id="email-dialog"
      open={open}
      title={t('admin->>JobStatusReport.EmailModal.Title')}
      formWrapper={formWrapper}
      message={
        <Box>
          <Box>
            <InputLabel required error={!!errors.emails}>
              {t('admin->>JobStatusReport.EmailModal.EmailRecipients')}
            </InputLabel>
            {fields.map((field, index) => (
              <Box key={field.id} className={classes.emailContainer}>
                <TextFieldForm
                  key={field.id}
                  form={form} // for field arrays only
                  name={`emails.${index}`}
                  type="text"
                  error={!!errors.emails?.[index]}
                  helperText={errors.emails?.[index]?.message?.toString()}
                  isRequired
                  placeholder={t('admin->>JobStatusReport.EmailModal.EmailPlaceholder')}
                />
                <Box className={classes.deleteIcon}>
                  {isDeletable && (
                    <IconButton
                      name={IconName.trash}
                      color={theme.color.icon.iconTrash}
                      ariaLabel="delete icon"
                      onClick={() => onDelete(index)}
                      iconSize="M" />
                  )}
                </Box>
              </Box>
            ))}
          </Box>
          <Box className={classes.addContainer}>
            <Button id="add-email" variant="text" onClick={onAdd}>
              {t('admin->>JobStatusReport.EmailModal.AddEmail')}
            </Button>
          </Box>

          <TextFieldForm
            form={form}
            label={t('admin->>JobStatusReport.EmailModal.Password')}
            name="password"
            type="text"
            error={!!errors.password}
            helperText={errors?.password?.message?.toString()}
            isRequired
          />
        </Box>
      }
      action={
        <SubmitActions
          onClose={onCancel}
          cancelString={t('admin->>JobStatusReport.EmailModal.Cancel')}
          submitString={t('admin->>JobStatusReport.EmailModal.Confirm')}
          apiStatus={mutationStatus}
        />
      }
    />)
  );
};

export default JobStatusReportEmailModal;
